import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { sdkRequest } from '@eventbrite/http';
import { pruneObject } from '@eventbrite/redux-destination';
import {
    deepKeysToSnake,
    keysCamelToSnake,
} from '@eventbrite/transformation-utils';
import { $FixMe } from '@eventbrite/ts-utils';
import Cookies from 'cookies-js';
import omit from 'lodash/omit';
import moment from 'moment-timezone';
import { formatUrl } from 'url-lib';
import { searchAllEvents } from '../../api/searchAllEvents';
import {
    OrganicEventSearchApiResponse,
    searchOrganicEvents,
} from '../../api/searchOrganicEvents';
import { CURRENT_FUTURE, QUALITY } from '../../constants';
import { EventSearch } from '../../types/index';

const searchAutocompleteSuggestionsUrl =
    '/api/v3/destination/search/autocomplete/';

const shouldShowDebugInfo = (
    isStaff: boolean,
    searchQueryFiltered?: {
        bbox?: $FixMe;
        hash?: string;
        clientTimezone?: any;
    },
) => {
    // Backend does not support debug info when deduping is used
    // https://github.com/eventbrite/search_service//blob/f272067cfc357b14742262b135379cc035985a8c/search_service/common/elasticsearch/builders/execute.py#L168
    if (searchQueryFiltered?.hash) {
        return false;
    }

    if (!HAS_WINDOW || !window.location) {
        return false;
    }

    return isStaff && window.location.hash === '#debug';
};

// Check if the client_timezone of the query is correct. If we
// received it from the server, it may not be correct because
// it was guessed with GeoIP rather than browser-side JS.
// There may not be any client_timezone at all, this is currently only
// sent for online searches.
const CLIENT_TIMEZONE_COOKIE_NAME = 'client_timezone';

const repairClientTimezone = (searchQuery: EventSearch) => {
    if (searchQuery.clientTimezone) {
        const momentGuessedTimezone = moment.tz.guess();

        if (
            momentGuessedTimezone &&
            searchQuery.clientTimezone !== momentGuessedTimezone
        ) {
            Cookies.expire(CLIENT_TIMEZONE_COOKIE_NAME);
            Cookies.set(CLIENT_TIMEZONE_COOKIE_NAME, momentGuessedTimezone);

            return {
                ...searchQuery,
                clientTimezone: momentGuessedTimezone,
            };
        }
    }

    return searchQuery;
};

export interface SearchRequestParameters {
    event_search: EventSearch;
    'expand.destination_event': string[];
    debug?: boolean;
    debug_experiment_overrides?: $FixMe;
}

export type SearchApiResponse = {
    data: OrganicEventSearchApiResponse;
    metrics?: SearchMetrics;
};

export type SearchMetrics = {
    searchQueryResponseTime: number;
    searchResultCount: number;
    searchResultCountUnattendable: number;
};

export const search = async ({
    searchQuery,
    experimentOverride,
    isStaff,
}: {
    searchQuery: EventSearch;
    experimentOverride?: $FixMe;
    isStaff?: boolean;
    continuation?: string;
}): Promise<SearchApiResponse> => {
    // The search API allows either a bbox or a WOF placeID but not both
    let searchQueryFiltered =
        searchQuery.bbox && searchQuery.places
            ? omit(searchQuery, 'places')
            : searchQuery;

    searchQueryFiltered = repairClientTimezone(searchQueryFiltered);

    const isPointRadiusActive = !!Object.keys(searchQueryFiltered.pointRadius)
        .length;

    let requestParameters: SearchRequestParameters = {
        event_search: deepKeysToSnake(
            pruneObject({
                ...searchQueryFiltered,
                dedup: true,
                page_size: 20,
                ...(isPointRadiusActive ? { places: [] } : {}),
            }),
        ),
        'expand.destination_event': [
            'primary_venue',
            'image',
            'ticket_availability',
            'saves',
            'event_sales_status',
            'primary_organizer',
            'public_collections',
        ],
    };

    if (shouldShowDebugInfo(isStaff || false, searchQueryFiltered)) {
        requestParameters = {
            ...requestParameters,
            debug: true,
        };
    }

    if (
        experimentOverride &&
        experimentOverride.experimentName &&
        experimentOverride.experimentName !== 'none'
    ) {
        requestParameters = {
            ...requestParameters,
            debug_experiment_overrides: {
                [experimentOverride.experimentName]: experimentOverride.variant,
            },
        };
    }

    const apiCallStartTime = new Date().getTime();

    const data = await searchAllEvents({
        ...(requestParameters as any),
        interfaceName: 'search',
        slots: searchQueryFiltered.page === 1 ? 2 : 1,
    });

    const searchQueryResponseTime = new Date().getTime() - apiCallStartTime;

    return {
        data,
        metrics: {
            searchQueryResponseTime,
            searchResultCount: data.events.results.length || 0,
            searchResultCountUnattendable: countUnattendableEvents(data),
        },
    };
};

export function countUnattendableEvents({
    events: { results = [], promoted_results = [] },
}: OrganicEventSearchApiResponse) {
    const events = [...results, ...promoted_results];
    const unattendableEvents = events.filter((event) => {
        const isNotOnSale =
            event.event_sales_status?.sales_status !== 'on_sale';

        // Series events should always be considered attendable
        const isNotRecurrent = !event.num_children || event.num_children <= 1;

        return isNotOnSale && isNotRecurrent;
    });

    return unattendableEvents.length;
}

interface SearchAutoCompleteParameters {
    placeId?: string;
    onlineEventsOnly?: boolean;
    query?: string;
}

export const searchAutocompleteSuggestions = ({
    placeId,
    onlineEventsOnly,
    query,
}: SearchAutoCompleteParameters) => {
    let queryParams: $FixMe = {
        placeId,
        q: query,
        completionTypes: ['event', 'query'],
        'expand.destination_event': ['primary_venue', 'image'],
    };

    if (onlineEventsOnly) {
        queryParams.onlineEventsOnly = true;
    }

    queryParams = keysCamelToSnake(queryParams);
    return sdkRequest(formatUrl(searchAutocompleteSuggestionsUrl, queryParams));
};

export const searchEventSuggestions = ({
    placeId,
    onlineEventsOnly,
}: {
    placeId: string;
    onlineEventsOnly: boolean;
}) =>
    searchOrganicEvents({
        event_search: {
            places: placeId ? [placeId] : [],
            online_events_only: onlineEventsOnly,
            dates: [CURRENT_FUTURE],
            sort: QUALITY,
            aggs: {
                organizertagsautocomplete_agg: { size: 50 },
                tags: {},
                dates: {},
            },
        },
        'expand.destination_event': [
            'primary_venue',
            'image',
            'ticket_availability',
            'saves',
        ],
    });
